import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { TrpcInputs, TrpcOutputs } from '../../config/trpc';
import { getDifferences } from '../../utils/findChangedKeys';

export type FlightSegmentInput = NonNullable<TrpcInputs['updateFlightSegment']>;
export type FlightSegmentOutput = TrpcOutputs['updateFlightSegment'];

type UseFlightSegmentForm = { methods: UseFormReturn<FlightSegmentInput> };
type Options = { isCreate: boolean };

const defaultOptions: Options = { isCreate: false };

const validationSchema = z.object({
  flight_number: z.string().nullish(),
  origin_airport_id: z.string().nullish(),
  destination_airport_id: z.string().nullish(),
  departs_at: z.coerce.date().nullish(),
  arrives_at: z.coerce.date().nullish(),
  aircraft_type: z.string().nullish(),
  aircraft_id: z
    .union([z.string(), z.coerce.number()])
    .transform((value) => (value === '' ? undefined : Number(value)))
    .pipe(z.coerce.number().nullish()),
  provider_id: z.string().nullish(),
});

export const useFlightSegmentsForm = (
  data?: Partial<FlightSegmentInput>,
  { isCreate }: Options = defaultOptions,
): UseFlightSegmentForm => {
  const { reset, getValues, setValue, ...methods } = useForm<FlightSegmentInput>({
    mode: 'onBlur',
    resolver: zodResolver(validationSchema),
    defaultValues: data,
  });

  useEffect(() => {
    const updateFields = () => {
      const changes = getDifferences<FlightSegmentInput>(getValues(), data);
      if (!changes.length) return;

      for (const [key, value] of changes) {
        // TODO: another infinite generic typings we need to sort out
        if (value !== undefined) (setValue as any)(key, value);
      }
    };

    // Run if we're not on create & we have data
    if (!isCreate && data) updateFields();
  }, [data, reset, getValues, setValue, isCreate]);

  return { methods: { ...methods, getValues, setValue, reset } };
};
